import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    inject,
} from '@angular/core';
import { Router } from '@angular/router';

import { EnvironmentConfig } from '../../../../client-config/index';
import { SBreactionConfig } from '../../../../core/services/cms/models';
import { DeviceService } from '../../../../core/services/device/device.service';
import { GtmService } from '../../../../core/services/gtm/gtm.service';
import { ReactionsService } from '../../../../core/services/reactions/reactions.service';
import { StorageService } from '../../../../core/services/storage/storage.service';
import { UserService } from '../../../../core/services/user/user.service';
import { WindowRefService } from '../../../../core/services/windowRef/window-ref.service';
import { sbReactionAnimeKeyFrames, sbReactionAnimeOptions } from '../../../constants/odds-card-constant';
import { ClickedOutDirective } from '../../../directives/clicked-out.directive';
import { ISurfaceBetEvent } from '../../../models/surface-bet-event.model';
import { SBSingleReaction } from '../../../models/surface-bet-module.model';
import { SharedPipesModule } from '../../../pipes/shared-pipes.module';
import { ITab } from '../../tabsPanel/tabs-panel.model';

@Component({
    selector: 'odds-card-surface-bet-reactions',
    templateUrl: './odds-card-surface-bet-reactions.component.html',
    styleUrls: ['./odds-card-surface-bet-reactions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ClickedOutDirective, NgClass, SharedPipesModule],
})
export class OddsCardSurfaceBetReactionsComponent implements OnInit, OnChanges {
    @Input() surfaceBet: ISurfaceBetEvent;
    @Input() sbReactionConfig: SBreactionConfig;
    @Input() singleReactionCount: SBSingleReaction;
    @Input() sportId: number;
    @Input() sportName: string;
    @Input() hubIndex: number;
    @Input() isEdp: boolean;
    @Output() reactionsLoaded: EventEmitter<boolean> = new EventEmitter();
    displayReactions: boolean;
    isLadbrokes: boolean;
    svg_Id: string;
    enable_reactions: boolean;
    emoji_selected: boolean;
    reactionJourney: number = 1;
    animationActive = false;
    globalCount: number = 0;
    reactionSaved: boolean = false;
    userReaction: string = '';
    emojiClickListener: Function;
    private readonly environment = inject(EnvironmentConfig).configuration;

    constructor(
        private cdf: ChangeDetectorRef,
        private reactionsService: ReactionsService,
        protected windowRef: WindowRefService,
        private user: UserService,
        private storage: StorageService,
        private gtmService: GtmService,
        private router: Router,
        private deviceService: DeviceService,
    ) {}

    ngOnInit(): void {
        this.isLadbrokes = this.environment.BRAND === 'ladbrokes';
        this.checkReactions();
    }

    closeDropdown(): void {
        if (this.reactionJourney === 2) {
            this.reactionJourney = 1;
            this.checkReactions();
            this.cdf.detectChanges();
        }
    }

    openReactions(): void {
        if (this.reactionJourney === 1) {
            this.reactionJourney = 2;
            this.sendGATracking('click');
        }
    }

    sendGATracking(cond: string): void {
        const activeTab = this.storage.get('activetab');
        const activePill = this.storage.get('activepill');

        const labelEvent =
            this.sportId === 0 || this.hubIndex
                ? 'Home'
                : this.sportId !== 0 && !this.isEdp
                  ? this.sportName
                  : this.isEdp === true
                    ? this.router.url.split('/')[2]
                    : '';
        const reactionsObject = {
            REACTION1: 'like',
            REACTION2: 'think',
            REACTION3: 'dislike',
        };
        const emojiSelectGtm = {
            'event': 'Event.Tracking',
            'component.CategoryEvent': 'surface bet module',
            'component.LabelEvent': labelEvent,
            'component.ActionEvent': 'emoji reaction',
            'component.PositionEvent': this.getPositoion(activeTab, activePill),
            'component.LocationEvent': this.surfaceBet.content,
            'component.EventDetails': cond === 'click' ? 'reaction board' : reactionsObject[cond as keyof typeof reactionsObject],
            'component.URLClicked': 'not applicable',
        };
        this.gtmService.push(emojiSelectGtm.event, emojiSelectGtm);
    }
    getPositoion(activeTab: ITab, activePill: string) {
        switch (true) {
            case this.deviceService.isDesktop:
                return this.sportId === 0 && !this.hubIndex
                    ? 'Featured'
                    : activeTab
                      ? activeTab?.title || activeTab?.name || activeTab?.label || activeTab
                      : '';
            case this.deviceService.isMobile:
                return activePill ? activePill : activeTab ? activeTab?.title || activeTab?.name || activeTab?.label || activeTab : '';
        }
        return '';
    }

    checkReactions(): void {
        this.displayReactions = false;
        if (this.sbReactionConfig?.enabled && !this.singleReactionCount) {
            this.generateReactionObject();
        } else if (this.sbReactionConfig?.enabled && this.singleReactionCount) {
            this.userReaction = this.singleReactionCount?.userReaction || '';
            this.reactionSaved = this.userReaction !== '' ? true : false;
            this.displayReactions = true;
            this.cdf.detectChanges();
            this.globalCount =
                (this.singleReactionCount?.reaction1_globalCount || 0) +
                (this.singleReactionCount?.reaction2_globalCount || 0) +
                (this.singleReactionCount?.reaction3_globalCount || 0);
            this.reactionsLoaded.emit(true);
            this.cdf.detectChanges();
        }
    }

    generateReactionObject(): void {
        this.singleReactionCount = {
            reaction1_globalCount: 0,
            reaction2_globalCount: 0,
            reaction3_globalCount: 0,
            userReaction: '',
        };
        this.globalCount = 0;
        this.userReaction = '';
        this.reactionSaved = false;
        this.displayReactions = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.checkReactions();
    }

    selectReaction(reactionId: string, svg_Id: string, surfaceBetId: string, domEl: HTMLDivElement): void {
        this.svg_Id = svg_Id;
        this.reactionJourney = 3;
        this.cdf.detectChanges();
        const keyframs = sbReactionAnimeKeyFrames;
        const options = sbReactionAnimeOptions;
        const emojis = this.windowRef.document.querySelector('.emoji_svg_animate');
        const keyframe = new KeyframeEffect(emojis, keyframs, options);
        const keyframeAnimation = new Animation(keyframe, document.timeline);
        keyframeAnimation.play();
        this.animatekeyframe(keyframeAnimation, domEl);
        if (this.userReaction !== reactionId) {
            const payloadPost = {
                reactionId,
                surfaceBetId,
                selectionId: this.surfaceBet.selectionId,
                custId: this.user.custId,
            };
            this.reactionsService.saveReaction(payloadPost, this.user.username, this.user.bppToken, this.reactionSaved).subscribe(
                (res) => {
                    type NumericKeys<T> = { [K in keyof T]: T[K] extends number ? K : never }[keyof T];
                    const newReactionKey: keyof SBSingleReaction = (reactionId.toLowerCase() + '_globalCount') as NumericKeys<SBSingleReaction>;
                    this.singleReactionCount[newReactionKey] = this.singleReactionCount[newReactionKey] ?? 0;
                    if (this.reactionSaved) {
                        const oldReactionKey: keyof SBSingleReaction = (this.userReaction.toLowerCase() + '_globalCount') as keyof SBSingleReaction;
                        this.singleReactionCount[oldReactionKey]--;
                    }
                    this.singleReactionCount[newReactionKey]++;
                    this.singleReactionCount.userReaction = this.userReaction = reactionId;
                    this.reactionSaved = true;
                    this.reactionJourney = 1;
                },
                (err) => {
                    console.error(err);
                    this.checkReactions();
                },
            );
        }
        this.sendGATracking(reactionId);
    }

    animatekeyframe(keyframeAnimation: Animation, domEl: HTMLDivElement): void {
        keyframeAnimation.finished.then(
            () => {
                this.renderAnimationStatus(domEl);
            },
            (e) => {
                this.renderAnimationStatus(domEl);
            },
        );
    }

    renderAnimationStatus(el: HTMLDivElement) {
        this.reactionJourney = 1;
        el.click();
        this.cdf.detectChanges();
    }
}
